/**
 * Scheduled local backups.
 *
 * Writes a full JSON snapshot of the user's records to a folder they choose on
 * their own computer, on a cadence, while the app is open. Uses the File System
 * Access API (Chrome/Edge). The chosen directory handle is kept in IndexedDB
 * (handles are not JSON-serialisable); cadence / retention / last-run are
 * device-local in localStorage, since a folder lives on one specific machine.
 *
 * Limitation: a web page can only write while it is open, so the schedule fires
 * on app load (and from the Back-up-now button), never in the background. The
 * always-on safety net is the server-side nightly dump (GitHub Actions -> R2).
 *
 * The JSON payload is byte-for-byte the same shape the Data Management "Export
 * Cloud Backup (JSON)" produces, so these files import through the same flow.
 */
import { DB, TABLES } from "./db";
import { Lists, Settings } from "./prefs";
import { toast, escapeHtml, $ } from "./dom";
import { openModal } from "../components/modal";
import { openMigrationModal } from "../components/configs";

const CFG_KEY = "enigma_backup_cfg_v1";
const FILE_PREFIX = "enigma-backup-";

export type Cadence = "off" | "daily" | "weekly" | "monthly";
interface BackupCfg { cadence: Cadence; retention: number; lastAt: string; folderName: string; }
const DEFAULT_CFG: BackupCfg = { cadence: "off", retention: 30, lastAt: "", folderName: "" };

function getCfg(): BackupCfg {
  try { return { ...DEFAULT_CFG, ...(JSON.parse(localStorage.getItem(CFG_KEY) || "null") || {}) }; }
  catch { return { ...DEFAULT_CFG }; }
}
function setCfg(patch: Partial<BackupCfg>): BackupCfg {
  const next = { ...getCfg(), ...patch };
  try { localStorage.setItem(CFG_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

/* ---------- payload (must match Data Management export exactly) ---------- */
export function buildBackupJson(): string {
  const stripUuid = (rows: any[]) => (rows || []).map(r => { const { _uuid, ...rest } = r; return rest; });
  const payload: Record<string, any> = { _version: 2, exportedAt: new Date().toISOString() };
  for (const t of TABLES) payload[t] = stripUuid(DB.table(t));
  payload._lists = Lists.all();
  payload._settings = Settings.get();
  return JSON.stringify(payload, null, 2);
}
function backupFileName(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return `${FILE_PREFIX}${stamp}.json`;
}

/* ---------- IndexedDB: persist the directory handle ---------- */
function idb(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const r = indexedDB.open("enigma-backup", 1);
    r.onupgradeneeded = () => r.result.createObjectStore("handles");
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbGet(key: string): Promise<any> {
  const db = await idb();
  return new Promise((res, rej) => {
    const q = db.transaction("handles", "readonly").objectStore("handles").get(key);
    q.onsuccess = () => res(q.result);
    q.onerror = () => rej(q.error);
  });
}
async function idbSet(key: string, val: any): Promise<void> {
  const db = await idb();
  return new Promise((res, rej) => {
    const q = db.transaction("handles", "readwrite").objectStore("handles").put(val, key);
    q.onsuccess = () => res();
    q.onerror = () => rej(q.error);
  });
}

export function fsApiSupported(): boolean {
  return typeof (window as any).showDirectoryPicker === "function";
}
async function getHandle(): Promise<any | null> {
  try { return (await idbGet("backupDir")) || null; } catch { return null; }
}
async function verifyPermission(handle: any, prompt: boolean): Promise<boolean> {
  if (!handle || !handle.queryPermission) return true;
  const opts = { mode: "readwrite" };
  if ((await handle.queryPermission(opts)) === "granted") return true;
  if (prompt && (await handle.requestPermission(opts)) === "granted") return true;
  return false;
}

async function chooseFolder(): Promise<boolean> {
  if (!fsApiSupported()) {
    toast("This browser can't save to a folder. Use Chrome or Edge, or the manual Export button.", "err");
    return false;
  }
  try {
    const handle = await (window as any).showDirectoryPicker({ id: "enigma-backup", mode: "readwrite" });
    await idbSet("backupDir", handle);
    setCfg({ folderName: handle.name || "(folder)" });
    return true;
  } catch (e: any) {
    if (e && e.name !== "AbortError") toast("Couldn't set the folder: " + (e?.message || e), "err");
    return false;
  }
}

async function prune(handle: any, keep: number): Promise<void> {
  if (!keep || keep < 1) return;
  const names: string[] = [];
  try {
    for await (const entry of handle.values()) {
      if (entry.kind === "file" && entry.name.startsWith(FILE_PREFIX) && entry.name.endsWith(".json")) {
        names.push(entry.name);
      }
    }
  } catch { return; }
  names.sort(); // timestamped names sort chronologically
  const excess = names.length - keep;
  for (let i = 0; i < excess; i++) {
    try { await handle.removeEntry(names[i]); } catch { /* ignore */ }
  }
}

async function writeToFolder(handle: any): Promise<string> {
  const name = backupFileName();
  const fh = await handle.getFileHandle(name, { create: true });
  const w = await fh.createWritable();
  await w.write(buildBackupJson());
  await w.close();
  await prune(handle, getCfg().retention);
  setCfg({ lastAt: new Date().toISOString() });
  return name;
}

async function backupNow(): Promise<void> {
  let handle = await getHandle();
  if (!handle) { if (!(await chooseFolder())) return; handle = await getHandle(); }
  if (!handle) return;
  if (!(await verifyPermission(handle, true))) { toast("Folder access wasn't granted.", "err"); return; }
  try {
    const name = await writeToFolder(handle);
    toast("Backup saved to " + (getCfg().folderName || "folder") + " · " + name);
  } catch (e: any) {
    toast("Backup failed: " + (e?.message || e), "err");
  }
}

/* ---------- scheduling ---------- */
function intervalMs(c: Cadence): number {
  return c === "daily" ? 864e5 : c === "weekly" ? 7 * 864e5 : c === "monthly" ? 30 * 864e5 : Infinity;
}
function isDue(cfg: BackupCfg): boolean {
  if (cfg.cadence === "off") return false;
  if (!cfg.lastAt) return true;
  return (Date.now() - new Date(cfg.lastAt).getTime()) >= intervalMs(cfg.cadence);
}

/** Called once on app load. Writes a backup only when auto-backup is on, a
 *  folder is set with permission already granted (no prompt is possible without
 *  a user gesture), and one is due for the chosen cadence. */
export async function maybeRunScheduledBackup(): Promise<void> {
  try {
    const cfg = getCfg();
    if (!isDue(cfg) || !fsApiSupported()) return;
    const handle = await getHandle();
    if (!handle) return;
    if (!(await verifyPermission(handle, false))) {
      toast("Auto-backup is on but needs folder access — open ⚙ → Data Management → Scheduled Backup.", "err");
      return;
    }
    const name = await writeToFolder(handle);
    console.info("[backup] scheduled backup written:", name);
    toast("Auto-backup saved · " + name);
  } catch (e) {
    console.error("[backup] scheduled run failed", e);
  }
}

/* ---------- settings modal ---------- */
export function openBackupSettings(): void {
  const cfg = getCfg();
  const supported = fsApiSupported();
  const last = cfg.lastAt ? new Date(cfg.lastAt).toLocaleString() : "never";
  const sel = (v: string) => (cfg.cadence === v ? "selected" : "");
  const body = `
    <div class="space-y-4 text-sm">
      ${supported ? "" : `<div class="panel-2 !border-rose-500/50 text-rose-300 p-2 text-xs">This browser can't write to a folder. Use Chrome or Edge — or the manual "Export Cloud Backup (JSON)" button.</div>`}
      <div class="panel-2 p-3">
        <h4 class="font-semibold text-sm mb-2 text-emerald-300">Backup folder</h4>
        <div class="flex items-center gap-2 flex-wrap">
          <button class="btn btn-ghost" onclick="chooseBackupFolder()" ${supported ? "" : "disabled"}>Choose folder…</button>
          <span class="text-[var(--muted)]">${cfg.folderName ? "Saving to: <b>" + escapeHtml(cfg.folderName) + "</b>" : "No folder chosen yet"}</span>
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">A full JSON snapshot of every record is written here. The file imports through ⚙ → Data Management → Import Backup.</div>
      </div>
      <div class="panel-2 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="label" for="bkCadence">Automatic backup</label>
          <select class="select" id="bkCadence">
            <option value="off" ${sel("off")}>Off</option>
            <option value="daily" ${sel("daily")}>Daily</option>
            <option value="weekly" ${sel("weekly")}>Weekly</option>
            <option value="monthly" ${sel("monthly")}>Monthly</option>
          </select>
          <div class="text-xs text-[var(--muted)] mt-1">Runs when you open the app if a backup is due. A web app can only save while it's open.</div>
        </div>
        <div>
          <label class="label" for="bkRetention">Keep last (files)</label>
          <input class="input" id="bkRetention" type="number" min="1" max="365" value="${cfg.retention}">
          <div class="text-xs text-[var(--muted)] mt-1">Older backups in the folder are pruned beyond this many.</div>
        </div>
      </div>
      <div class="text-xs text-[var(--muted)]">Last backup: <b>${escapeHtml(last)}</b></div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Close</button>
    <button class="btn btn-ghost" onclick="backupNowClick()" ${supported ? "" : "disabled"}>Back up now</button>
    <button class="btn btn-ghost" onclick="openRestorePicker()">Restore…</button>
    <button class="btn btn-primary" onclick="saveBackupSchedule()">Save schedule</button>`;
  openModal({ title: "Scheduled Backup", body, footer });
}

function saveBackupSchedule(): void {
  const cadence = (($("#bkCadence") as HTMLSelectElement | null)?.value || "off") as Cadence;
  let retention = Number(($("#bkRetention") as HTMLInputElement | null)?.value) || 30;
  retention = Math.max(1, Math.min(365, Math.round(retention)));
  setCfg({ cadence, retention });
  if (cadence !== "off" && !getCfg().folderName) {
    toast("Pick a backup folder so automatic backups have somewhere to go.", "err");
  } else {
    toast("Backup schedule saved.");
  }
  openBackupSettings();
}

/* ---------- restore ---------- */
async function listBackupFiles(handle: any): Promise<string[]> {
  const names: string[] = [];
  try {
    for await (const entry of handle.values()) {
      if (entry.kind === "file" && entry.name.startsWith(FILE_PREFIX) && entry.name.endsWith(".json")) {
        names.push(entry.name);
      }
    }
  } catch { /* ignore */ }
  return names.sort().reverse(); // newest first
}

function prettyStamp(name: string): string {
  const m = name.match(/^enigma-backup-(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})\.json$/);
  if (!m) return name;
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
}

async function readAndRestore(getText: () => Promise<string>): Promise<void> {
  let text: string;
  try { text = await getText(); }
  catch (e: any) { toast("Couldn't read the file: " + (e?.message || e), "err"); return; }
  let payload: any;
  try { payload = JSON.parse(text); }
  catch (e: any) { toast("That file isn't valid JSON: " + (e?.message || e), "err"); return; }
  if (!payload || typeof payload !== "object") { toast("That file isn't a valid Enigma backup.", "err"); return; }
  // Hand off to the existing preview + wipe/replace flow (Data Management import).
  openMigrationModal(payload);
}

async function restoreSelectedBackup(): Promise<void> {
  const name = ($("#rsFile") as HTMLSelectElement | null)?.value || "";
  if (!name) { toast("Pick a backup file first.", "err"); return; }
  const handle = await getHandle();
  if (!handle) { toast("No backup folder set.", "err"); return; }
  await readAndRestore(async () => {
    const fh = await handle.getFileHandle(name);
    const file = await fh.getFile();
    return file.text();
  });
}

async function restoreFromAnyFile(): Promise<void> {
  if (typeof (window as any).showOpenFilePicker !== "function") {
    toast("Use the gear menu → Data Management → Import Backup to restore a file in this browser.", "err");
    return;
  }
  try {
    const picked = await (window as any).showOpenFilePicker({
      types: [{ description: "Enigma backup (JSON)", accept: { "application/json": [".json"] } }],
      multiple: false,
    });
    const fh = picked[0];
    const file = await fh.getFile();
    await readAndRestore(() => file.text());
  } catch (e: any) {
    if (e && e.name !== "AbortError") toast("Couldn't open the file: " + (e?.message || e), "err");
  }
}

export async function openRestorePicker(): Promise<void> {
  const handle = await getHandle();
  if (handle && (await verifyPermission(handle, true))) {
    const names = await listBackupFiles(handle);
    const options = names.length
      ? names.map(n => `<option value="${escapeHtml(n)}">${escapeHtml(prettyStamp(n))}  ·  ${escapeHtml(n)}</option>`).join("")
      : `<option value="">(no backups found in this folder)</option>`;
    const body = `
      <div class="space-y-4 text-sm">
        <div class="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-200 text-xs">
          Restoring <b>replaces</b> everything currently in your account with the chosen backup. You'll get a preview and a final confirm before anything changes.
        </div>
        <div class="panel-2 p-3">
          <label class="label" for="rsFile">Backup from <b>${escapeHtml(getCfg().folderName || "folder")}</b></label>
          <select class="select" id="rsFile">${options}</select>
          <div class="text-xs text-[var(--muted)] mt-1">Newest first.</div>
        </div>
      </div>`;
    const footer = `
      <button class="btn btn-ghost" onclick="closeModal()">Close</button>
      <button class="btn btn-ghost" onclick="restoreFromAnyFile()">Pick a file instead…</button>
      <button class="btn btn-primary" onclick="restoreSelectedBackup()" ${names.length ? "" : "disabled"}>Preview &amp; restore</button>`;
    openModal({ title: "Restore from backup", body, footer });
    return;
  }
  // No folder set (or permission denied) — fall straight to a file picker.
  await restoreFromAnyFile();
}

(window as any).openBackupSettings = openBackupSettings;
(window as any).saveBackupSchedule = saveBackupSchedule;
(window as any).chooseBackupFolder = () => { void chooseFolder().then(() => openBackupSettings()); };
(window as any).backupNowClick = () => { void backupNow().then(() => openBackupSettings()); };
(window as any).openRestorePicker = () => { void openRestorePicker(); };
(window as any).restoreSelectedBackup = () => { void restoreSelectedBackup(); };
(window as any).restoreFromAnyFile = () => { void restoreFromAnyFile(); };
